import React, { useState } from 'react';
import { useProjectAnalysis, useLocalStorage } from '@/hooks/use-api';
import { APIUtils } from '@/utils/api-utils';
import {
  Code2,
  FolderOpen,
  Play,
  Loader2,
  AlertTriangle,
  CheckCircle,
  GitBranch,
  Package,
  Lightbulb
} from 'lucide-react';
import toast from 'react-hot-toast';

function ProjectAnalysis() {
  const [projectPath, setProjectPath] = useLocalStorage('analysis-path', '/my-project');
  const [language, setLanguage] = useLocalStorage('analysis-language', '');
  const [framework, setFramework] = useLocalStorage('analysis-framework', '');

  const { analysis, loading, error, analyze } = useProjectAnalysis();

  const handleAnalyze = async () => {
    if (!APIUtils.isValidProjectPath(projectPath)) {
      toast.error('Please enter a valid project path (e.g., /my-project)');
      return;
    }

    const request = {
      path: projectPath,
      ...(language && { language }),
      ...(framework && { framework }),
    };

    toast.promise(
      analyze(request),
      {
        loading: 'Analyzing project...',
        success: 'Project analysis completed',
        error: (err) => err || 'Failed to analyze project',
      }
    );
  };

  const commonLanguages = [
    { value: '', label: 'Auto-detect' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'csharp', label: 'C#' },
    { value: 'php', label: 'PHP' },
  ];

  const commonFrameworks = [
    { value: '', label: 'Auto-detect' },
    { value: 'react', label: 'React' },
    { value: 'vue', label: 'Vue.js' },
    { value: 'angular', label: 'Angular' },
    { value: 'django', label: 'Django' },
    { value: 'spring', label: 'Spring Boot' },
    { value: 'express', label: 'Express.js' },
    { value: 'nextjs', label: 'Next.js' },
    { value: 'nestjs', label: 'NestJS' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Project Analysis</h1>
        <p className="text-gray-600 mt-2">
          Analyze your project to detect languages, frameworks, dependencies, and get recommendations
        </p>
      </div>

      {/* Analysis Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Configuration</h2>

        <div className="space-y-4">
          {/* Project Path */}
          <div>
            <label htmlFor="project-path" className="block text-sm font-medium text-gray-700 mb-2">
              Project Path
            </label>
            <div className="relative">
              <FolderOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                id="project-path"
                value={projectPath}
                onChange={(e) => setProjectPath(e.target.value)}
                placeholder="/path/to/your/project"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Enter the absolute path to your project directory
            </p>
          </div>

          {/* Language Selection */}
          <div>
            <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
              Programming Language (Optional)
            </label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {commonLanguages.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          {/* Framework Selection */}
          <div>
            <label htmlFor="framework" className="block text-sm font-medium text-gray-700 mb-2">
              Framework (Optional)
            </label>
            <select
              id="framework"
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {commonFrameworks.map((fw) => (
                <option key={fw.value} value={fw.value}>
                  {fw.label}
                </option>
              ))}
            </select>
          </div>

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={loading || !projectPath.trim()}
            className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Code2 className="w-5 h-5" />
                <span>Analyze Project</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Analysis Failed</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="space-y-6">
          {/* Project Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Name:</span>
                <p className="font-medium">{analysis.projectInfo.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Path:</span>
                <p className="font-mono text-sm">{analysis.projectInfo.path}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Language:</span>
                <p className="font-medium">{analysis.projectInfo.language || 'Auto-detected'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Framework:</span>
                <p className="font-medium">{analysis.projectInfo.framework || 'Unknown'}</p>
              </div>
            </div>
          </div>

          {/* Detected Ecosystems */}
          {analysis.ecosystems && analysis.ecosystems.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Detected Ecosystems</h2>
              <div className="space-y-3">
                {analysis.ecosystems.map((ecosystem: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <GitBranch className="w-5 h-5 text-primary-600" />
                      <div>
                        <p className="font-medium">{ecosystem.name}</p>
                        <p className="text-sm text-gray-600">{ecosystem.language}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{ecosystem.packageManager}</p>
                      <p className="text-xs text-gray-500">{ecosystem.version || 'Latest'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dependencies */}
          {analysis.dependencies && analysis.dependencies.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Dependencies ({analysis.dependencies.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysis.dependencies.slice(0, 10).map((dep: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Package className="w-4 h-4 text-gray-600" />
                      <span className="font-medium">{dep.name}</span>
                    </div>
                    <span className="text-sm text-gray-600">{dep.version}</span>
                  </div>
                ))}
              </div>
              {analysis.dependencies.length > 10 && (
                <p className="text-sm text-gray-500 mt-3">
                  ... and {analysis.dependencies.length - 10} more dependencies
                </p>
              )}
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Recommendations ({analysis.recommendations.length})
              </h2>
              <div className="space-y-3">
                {analysis.recommendations.map((rec: any, index: number) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                    <Lightbulb className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-900">{rec.title || 'Recommendation'}</p>
                      <p className="text-sm text-blue-800 mt-1">{rec.description}</p>
                      {rec.priority && (
                        <span className={`inline-block mt-2 px-2 py-1 text-xs font-semibold rounded-full ${
                          rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                          rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {rec.priority} priority
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => window.open('/suggestions', '_blank')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Lightbulb className="w-4 h-4" />
              <span>Get Cross-Language Suggestions</span>
            </button>
            <button
              onClick={() => window.open('/ai-routing', '_blank')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-secondary-600 text-white rounded-lg hover:bg-secondary-700 transition-colors"
            >
              <Code2 className="w-4 h-4" />
              <span>Optimize with AI</span>
            </button>
          </div>
        </div>
      )}

      {/* Example Usage */}
      {!analysis && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Example Usage</h3>
          <div className="space-y-3 text-sm">
            <div>
              <code className="bg-blue-100 px-2 py-1 rounded">Project Path:</code>
              <span className="ml-2 text-blue-800">/Users/username/my-react-app</span>
            </div>
            <div>
              <code className="bg-blue-100 px-2 py-1 rounded">Language:</code>
              <span className="ml-2 text-blue-800">JavaScript (optional)</span>
            </div>
            <div>
              <code className="bg-blue-100 px-2 py-1 rounded">Framework:</code>
              <span className="ml-2 text-blue-800">React (optional)</span>
            </div>
          </div>
          <p className="text-blue-800 mt-3 text-sm">
            The analysis will detect package.json files, dependencies, project structure, and provide recommendations for improvements and cross-language migration options.
          </p>
        </div>
      )}
    </div>
  );
}

export default ProjectAnalysis;